import { faBookmark, faChartSimple, faCircleEllipsis, faComment, faHeart } from '@fortawesome/pro-regular-svg-icons';
import {
    faBookmark as faBookmarkFull,
    faComment as faCommentFull,
    faGrid2,
    faHeart as faHeartFull,
} from '@fortawesome/pro-solid-svg-icons';
import './AdvancedMediaViewerOverlay.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from "@tanstack/react-query";
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { USER_CURRENT_KEY } from "../../../../../api/public-voissa/users";
import useAbility from "../../../../../hooks/useAbility";
import useLike, { LIKE_KINDS } from '../../../../../hooks/useLike';
import useMediaBookMark from '../../../../../hooks/useMediaBookmark';
import createLink from '../../../../../libs/createLink';
import { convertDateToStrDuration, genererViews, isNoFollowAlbum } from '../../../../../libs/helper';
import Reader from "../../../../../libs/lexical/Reader";
import Avatar from '../../../../components/Avatar';
import Comments from '../../../../components/comments/Comments';
import CertifiedBadge from '../../../CertifiedBadge';
import Drawer from "../../../Drawer/Drawer";
import Button from "../../../UI/Button/Button";
import AdvancedMediaViewerStats from "../AdvancedMediaViewerStats/AdvancedMediaViewerStats";
import AdvancedMediaViewerOptions from "../AdvancedMediaViewerOptions/AdvancedMediaViewerOptions";
import useProfileFollow from '../../../../../hooks/useProfileFollow';

const AdvancedMediaViewerItemActions = ({
    media, 
    setToggleSlider,
    handleClose
})=>{
    const [toggleComments, setToggleComments] = useState(false);
    const [toggleActions, setToggleActions] = useState(false);
    const [toggleStats, setToggleStats] = useState(false);
    const queryClient = useQueryClient();
    const [bookmarkCount, setBookmarkCount] = useState(media?.count?.bookmark || 0);
    const dataCurrentUser = queryClient.getQueryData([USER_CURRENT_KEY]);

    const {can} = useAbility();
    const canStats =  can({ action:'update', subject: media, modelName : 'Media' });

    const {isLiked, handleLike} = useLike({
        parentKind : LIKE_KINDS.MEDIA,
        parentId : media._id,
        isLike : media.isLiked,
        contentOwner : media.owner
    });
    
    const {isBookmarked, handleBookmark} = useMediaBookMark({
        isBookmarked : media.isBookmarked,
        mediaId : media._id,
        onMutate : () =>{
            setBookmarkCount(isBookmarked ? bookmarkCount -1 : bookmarkCount +1);
        },
        onError : () =>{
            setBookmarkCount(media?.count?.bookmark || 0);
        }
    });

    const handleToggleComments = useCallback(() =>{
        setToggleSlider(true);
        setToggleComments(!toggleComments);
    }, [setToggleSlider,toggleComments, setToggleComments]);

    const handleToggleActions = useCallback(() =>{
        setToggleSlider(true);
        setToggleActions(!toggleActions);
    }, [toggleActions, setToggleSlider, setToggleActions]);

    const handleToggleStats = useCallback(() =>{
        setToggleSlider(true);
        setToggleStats(!toggleStats);
    }, [toggleStats, setToggleSlider, setToggleStats]);
    
    return (
        <>
            <div className="amv-item-actions">
                <div className="amv-item-action" onClick={handleLike}>
                    {isLiked ? <FontAwesomeIcon icon={faHeartFull} className="liked"/> : <FontAwesomeIcon icon={faHeart}/>}
                    <span>{genererViews(media?.count?.like || 0)}</span>
                </div>
                <div className="amv-item-action" onClick={()=>setToggleComments(toggleComments ? null :  media)}>
                    {toggleComments?._id ? <FontAwesomeIcon icon={faCommentFull}/> : <FontAwesomeIcon icon={faComment}/>}
                    <span>{genererViews(media?.count?.comment)}</span>
                </div>
                <div className="amv-item-action" onClick={handleBookmark}>
                    {isBookmarked ? <FontAwesomeIcon icon={faBookmarkFull} className="bookmarked"/> : <FontAwesomeIcon icon={faBookmark}/>}
                    <span>{genererViews(bookmarkCount)}</span>
                </div>
                {canStats && (
                    <div className="amv-item-action" onClick={() => setToggleStats(!toggleStats)}>
                        <FontAwesomeIcon icon={faChartSimple}/>
                    </div>
                )}
                {dataCurrentUser?._id && (<div className="amv-item-action" onClick={()=>setToggleActions(!toggleActions)}>
                    <FontAwesomeIcon icon={faCircleEllipsis}/>
                </div>)}
            </div>
            <Drawer 
                    open={toggleComments} 
                    handleClose={handleToggleComments} 
                    headContent={`${media?.count?.comment || 0} Commentaires`}
                    >
                <Comments
                    type={"medias"}
                    parent={media}
                    commentsAllowed={!!media?.comment?.allowed}
                    className='drawer-comments'
                />
            </Drawer>
            <Drawer
                open={toggleActions}
                handleClose={handleToggleActions}
                headContent="Options de la publication"
                xsmall
            >
                <AdvancedMediaViewerOptions media={media} handleClose={handleClose}/>
            </Drawer>
            <Drawer
                open={toggleStats}
                handleClose={handleToggleStats}
                headContent="Stats de la publication"
                small
            >
                <AdvancedMediaViewerStats parentId={media?._id} type={"medias"} stats={media?.count || {}} />
            </Drawer>
        </>
    )
}

const AdvancedMediaViewerOverlay = ({media, isUserInactive, sliderEnable, setSliderEnable, handleCloseAMV, children}) => {
    const [toggleCategories, setToggleCategories] = useState(null);
    const [showMoreButton, setShowMoreButton] = useState(false);
    const [toggleViewMore, setToggleViewMore] = useState(false);

    const viewMoreRef = useRef(null);

    const {followState } = useProfileFollow({user : media?.owner, isRequest : !!media?.owner});

    useEffect(() => {
        const componentHeight = viewMoreRef?.current?.clientHeight;
        const componentScrollHeight = viewMoreRef?.current?.scrollHeight;
        if(!toggleViewMore) setShowMoreButton(componentScrollHeight > componentHeight);
    }, [toggleViewMore, viewMoreRef.current?.clientHeight, viewMoreRef.current?.scrollHeight]);

    function gotoUrl(uri){
        uri = uri ? createLink('facette', {slug : uri}) : false;
        if(uri){
            window.location = uri;
        }
    }

    const relLinkAlbum = isNoFollowAlbum(media) ? { rel: "nofollow" } : {};
    const relLinkProfil = !media?.owner?.isIndexed ? { rel: "nofollow" } : {};
    const currentFollowState = [
        {label : '', cssClass : 'follow'},
        {label : '• Suivi(e)', cssClass : 'unfollow'},
        {label : '', cssClass : 'follow-back'}
    ][followState || 0];
    const isInactive = isUserInactive || !sliderEnable;

    return (
        <div className={classNames("amv-item-container-overlay", {player:(media.kind === 'video'), 'user-is-inactive':isInactive})}>
            <AdvancedMediaViewerItemActions 
                media={media} 
                setToggleSlider={setSliderEnable} 
                toggleSlider={sliderEnable}
                handleClose={handleCloseAMV}
                />
            {media.owner && (
                <div className={classNames("amv-item-publication-owner-wrapper", {locked: media.locked})}>
                    <Link className="amv-item-publication-owner" to={createLink("userProfile", media.owner)} {...relLinkProfil}>
                        <Avatar avatar={media.owner.profile?.avatar}
                                height={40}
                                width={40}
                                online={media.owner.online || false}
                                gender={media.owner.profile?.gender || "guest"}
                                alt=""/>
                        <div className="amv-item-publication-owner-infos">
                            <span className="username">{media.owner.username} {!!media.owner.isCertified && <CertifiedBadge style={{marginLeft:'4px'}}/>}
                                <span className={`username-follow-status ${currentFollowState.cssClass}`} >{currentFollowState.label}</span>
                            </span>
                            {media.createdAt && <span className="elapsed-time">{convertDateToStrDuration(media.createdAt)}</span>}
                        </div>
                    </Link>
                </div>
            )}
            <div className="amv-item-infos">
                {media.album && (
                    <Link className="amv-item-collection"
                    to={createLink("userCollection", {
                        ...media.owner, album : media.album
                    })} {...relLinkAlbum}>
                        <FontAwesomeIcon icon={faGrid2}/> {media.album.title}
                    </Link>
                )}
                {media.legend && <p onClick={()=>setToggleViewMore(!toggleViewMore)} ref={viewMoreRef} className={classNames({more:toggleViewMore, 'has-more':showMoreButton})}>
                    <Reader text={media?.legend || ""} meta={{
                        mention : media.mentions,
                        link: media.links
                    }}/>
                </p>}
                {showMoreButton && <div className='show-more' onClick={()=>setToggleViewMore(!toggleViewMore)}>{toggleViewMore ? 'Moins' : 'Plus'}</div>}
                
                {media.categories && media.categories.length > 0 && (
                    <div className="amv-item-tags">
                        {media.categories.slice(0, 5).map((category, index) => (
                            category.isUri ? (
                                <Link className='amv-item-tag' to={createLink('facette', {slug : category.uri})} key={index}>{category.title}</Link>
                                ): (
                                <span className='amv-item-tag' onClick={() => gotoUrl(category.uri)} key={index}>{category.title}</span>
                                )
                        ))}
                        {media.categories.length > 5 && (
                            <span className='amv-item-tag' onClick={() => setToggleCategories(true)}>+ {media.categories.length - 5}</span>
                        )}
                    </div>
                )}
            </div>
            {!!children && <div className="amv-item-overlay-children">{children}</div>}
            <Drawer
                open={toggleCategories}
                handleClose={() => setToggleCategories(null)}
                headContent={`Categories`}
            >
                <div className="amv-tags-list">
                    {media?.categories?.map((category, index) => (
                        <Button
                            key={index}
                            isLink={category.isUri}
                            style={{margin : "4px"}}
                            to={createLink('facette', {slug: category.uri})}
                            onClick={() => gotoUrl(category.uri)}
                        >
                            {category.title}
                        </Button>
                    ))}
                </div>
            </Drawer>
        </div>
    );
};

export default AdvancedMediaViewerOverlay;